import { useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { DEFAULT_INSIGHT_TAGS_BY_USER_ROLE, INSIGHT_ACTIONS, REMIRROR_BLOT_TYPES } from '../../../constants';
import { artifactAtom } from '../../../stores/jotai/artifacts/artifacts';
import { baseAtom, getCurrentArtifactID, selectInsight, toggleAllFavoritedVisibility, updateBaseFinding } from '../../../stores/jotai/base';
import { canvasAtom, loadLibraryExternally } from '../../../stores/jotai/canvas/canvas';
import { discoverySessionAtom } from '../../../stores/jotai/discovery-session';
import { interviewAtom } from '../../../stores/jotai/interviews/currentInterview';
import { hasPermissionCanvasWrite } from '../../../stores/jotai/permissions';
import { useGetCanvasLayout } from '../../../tanstackQuery/queries/canvas';
import { useCreateCustomFinding, useDeleteCustomFinding, useUpdateCustomFinding } from '../../../tanstackQuery/queries/custom-finding-types';
import { useCreateFinding, useDeleteFinding, useUpdateFinding } from '../../../tanstackQuery/queries/findings';
import MaterialQuoteView from '../material-quote-view';
import { FindingEditor } from '.';
import { toast } from 'sonner';
import { renderErrorToast } from '../../../../lib/ErrorToastMessage';
import { selectedInsightRowAtom } from '../../../stores/jotai/insights/selectedInsightRow';
import AlertDialog from '../../app/alert-dialog';
import { Button, CircularProgress } from '@mui/material';
import { DELETE_INSIGHT_MODAL_TEXTS } from '../../canvas/constants';
import { canSplit } from '@remirror/pm/transform';

export default function MaterialFindingEditor(props) {
    const {
        selectedArtifactType,
        user,
        team,
        appState,
        cohort,
        canvasCategories,
        canvasEntries,
        canvas,
        findingTypes,
        isSaving,
        clearSelectedFinding,
        findingLinksData,
    } = props;
    const interviewState = useAtomValue(interviewAtom);
    const [base, setBase] = useAtom(baseAtom);
    const setCanvasState = useSetAtom(canvasAtom);
    const [createSaving, setCreateSaving] = useState(false);
    const [deletingInsight, setDeletingInsight] = useState<boolean>(false);
    const [insightToDelete, setInsightToDelete] = useState<string>(null);
    const { keycloak } = useKeycloak()
    const [quoteCache, setQuoteCache] = React.useState<{textCache: string} | null>(null);
    const canEditCanvas = hasPermissionCanvasWrite(base);
    const isInstructor = base.roleSnapshot.isInstructor;
    const discoverySession = useAtomValue(discoverySessionAtom);
    const [selectedInsightRow, setSelectedInsightRow] = useAtom(
        selectedInsightRowAtom
    );
    const theme = useTheme();
    const artifacts = useAtomValue(artifactAtom);
    const client = useQueryClient();
    const customFindingTypes = client.getQueryData(["custom-finding-types", { groupID: base.team.groupID }]) as any[];
    
    const { mutateAsync: createFinding } = useCreateFinding(
      { accessToken: keycloak.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["insights"] });
          client.invalidateQueries({ queryKey: ["finding-links"] });
        }
      }
    );

    const { mutateAsync: updateFinding } = useUpdateFinding(
      { accessToken: keycloak.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["insights"] });
          client.invalidateQueries({ queryKey: ["finding-links"] });
        }
      }
    );

    const { mutateAsync: createCustomFindingType } = useCreateCustomFinding(
      { accessToken: keycloak.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["custom-finding-types"] });
        }
      }
    );

    const { mutateAsync: updateCustomFindingType } = useUpdateCustomFinding(
      { accessToken: keycloak.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["custom-finding-types"] });
        }
      }
    );

    const { mutateAsync: deleteCustomFindingType } = useDeleteCustomFinding(
      { accessToken: keycloak.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["custom-finding-types"] });
        }
      }
    );
    const { mutateAsync: deleteFinding } = useDeleteFinding(
      { accessToken: keycloak.token },
      {
        onSuccess: () => {
            client.invalidateQueries({ queryKey: ["insights"] });
        },
      }
    );

    const { data: canvasLayout, isLoading: isLayoutLoading } =
      useGetCanvasLayout({ accessToken: keycloak.token });

    const delay = 150;
    React.useEffect(() => {
        const id = setInterval(() => {
            // @ts-ignore
            setQuoteCache(window.__lastInsightSelection);
            // @ts-ignore
            if (isEqual(quoteCache, window.__lastInsightSelection) === false) {
                if (base.selectedFinding !== undefined) {
                    //selectInsight(setBase, undefined);
                }
            }
        }, delay)

        return () => clearInterval(id)
    }, [delay, quoteCache, base.selectedFinding])

    React.useEffect(() => {
        loadLibraryExternally(setCanvasState,canvasLayout);
    }, [canvasLayout,isLayoutLoading]);

    React.useEffect(() => {
        if (
            base.selectedFinding &&
            base.selectedFinding.interviewID !== base.selectedInterview
        ) {
            selectInsight(setBase, undefined);
        }
    }, [base.selectedFinding, base.selectedInterview])

    let defaultTypes = [];

    try {
        let typeIds = [];

        if (!!base.selectedFinding) {
            for (let i = 0; i < base.selectedFinding.joinery.length; i++) {
                typeIds.push(base.selectedFinding.joinery[i].customFindingTypeID);
            }

            for (let i = 0; i < typeIds.length; i++) {
                for (let c = 0; c < customFindingTypes.length; c++) {
                    if (
                        customFindingTypes[c].customFindingTypeID === typeIds[i]
                    ) {
                        defaultTypes.push({
                            ...customFindingTypes[c],
                            value: customFindingTypes[c].customFindingTypeID,
                        });
                    }
                }
            }
        } else if (!!quoteCache?.textCache) {
          customFindingTypes.forEach(type => {
                if (
                        (isInstructor &&
                            type.label ===
                                DEFAULT_INSIGHT_TAGS_BY_USER_ROLE.INSTRUCTOR)
                    ) {
                        defaultTypes.push(type);
                    }
            });
        }
    }
    catch (e) {
        //console.log('t');
    }

    let token = undefined;

    try {
        token = keycloak.token;
    }
    catch (e) {
    }


    // figure out which finding to display

    // first check to see if one is selected in the menu
    let _finding = undefined;

    try {
        _finding = base.selectedFinding;
    }
    catch (e) {

    }

    // also keep track of our quote
    let __quote = null;

    // try older/misc quote format types to try
    // and catch an inline migration
    try {
        __quote = JSON.parse(_finding.quote);
    } catch (e) { 
        if (__quote === undefined || __quote === null) __quote = "";
    }

    // if there's not an active finding, let's show our
    // current placeholder insight instead
    if (_finding === undefined) {
        if (quoteCache !== undefined) {
            __quote = quoteCache;
        }
    }
    // if one is never found, use an empty tag set
    if (__quote === "") {
        defaultTypes = [];
    }
     const deleteInsight = (insight) => {
        setDeletingInsight(true);
    
        deleteFinding({
          findingID: insight?.findingID,
          sessionID: discoverySession?.sessionID,
        })
          .then(() => {
            setInsightToDelete(null);
    
            if (base?.selectedFinding?.findingID === insight.findingID) {
              selectInsight(setBase, undefined);
            }
    
            if (
              selectedInsightRow?.selectedFinding?.findingID === insight.findingID
            ) {
              setSelectedInsightRow(null);
            }
    
            window.postMessage(
              `remirrorFormat~~${REMIRROR_BLOT_TYPES.INSIGHT}~~${INSIGHT_ACTIONS.DELETE}~~${insight?.findingID}`
            );
          })
          .finally(() => {
            setDeletingInsight(false);
          });
      };
    
      const handleDeletion = (insight) => {
        setInsightToDelete(insight);
      };
      
    return (
      <>
      <FindingEditor
        findingLinksData={findingLinksData}
        customFindingTypes={customFindingTypes as any[]}
        activeArtifactName={selectedArtifactType}
        isDraft={!!_finding?.isDraft}
        user={user}
        team={team}
        base={base}
        theme={theme}
        appState={appState}
        cohort={cohort}
        canvasCategories={canvasCategories}
        canvasEntries={canvasEntries}
        canvas={canvas}
        findingTypes={findingTypes}
        defaultTypes={defaultTypes}
        finding={_finding}
        clearSelectedFinding={clearSelectedFinding}
        quote={__quote}
        interviewID={base.selectedInterview}
        interviewState={interviewState}
        isSaving={isSaving}
        createSaving={createSaving}
        canEditCanvas={canEditCanvas}
        onDelete={() => {
          handleDeletion(base.selectedFinding);
        }}
        renderMaterialArtifact={() => {
          return (
            <MaterialQuoteView
              artifactID={getCurrentArtifactID(base, artifacts)}
              selection={__quote}
            />
          );
        }}
        baseCreateCategory={async (payload) => {
          await createCustomFindingType({
            ...payload,
            userID: base.user.userID,
            teamID: base.team.groupID
          })
            .then(() => {})
            .catch(() => {});
        }}
        baseUpdateCategory={async (findingObject, changes) => {
          // Changes is an object of only the top level attributes that are changing (eg. color, label, options)
          await updateCustomFindingType({
            ...findingObject,
            ...changes
          })
            .then(() => {})
            .catch(() => {});
        }}
        baseDeleteCategory={async (id) => {
        await deleteCustomFindingType({customFindingTypeID: id}).then(() => {}).catch(() => {})
        }}
        baseToggleAllFavoritedTags={() => {
          toggleAllFavoritedVisibility(setBase, base);
        }}
        create={(comment, types: any, hypothesisSupporters, rating) => {
          setCreateSaving(true);

          const {user,} = props;

          try {
            // Swap head and anchor if anchor is greater than head
            // We get quote both as string and object here
            const quote = JSON.parse(JSON.stringify(__quote));
            const anchor = quote?.selection?.anchor;
            const head = quote?.selection?.head;
            if (anchor > head) {
              quote.selection.anchor = head;
              quote.selection.head = anchor;
            }
            __quote = quote;
          } catch (error) {
            renderErrorToast("Swap error");
          }

          const findingDetails = {
            editor: "material", // used to calculate newline offsets. Only for notesEditor ATTOW.
            artifactID: getCurrentArtifactID(base, artifacts),
            audioBegin: __quote?.audioBegin,
            audioEnd: __quote?.audioEnd,
            begin: props.begin,
            end: props.end,
            comment: comment,
            created: new Date(),
            typeID: types,
            quote: __quote,
            userID: user.id,
            interviewID: base.selectedInterview,
            hypothesisLinks: JSON.stringify(hypothesisSupporters),
            rating,
            sessionID: discoverySession?.sessionID
          };

          createFinding(findingDetails).then((value) => {
            setCreateSaving(false);
            const finding = { ...value, hypothesisLinks: hypothesisSupporters,findingInsertionType: "Human", };
            updateBaseFinding(setBase, finding);

            try {
              const { findingID } = value;

              window.postMessage(
                `remirrorFormat~~${REMIRROR_BLOT_TYPES.INSIGHT}~~${INSIGHT_ACTIONS.CREATE}~~${findingID}`
              );
            } catch (e) {}
          });
        }}
        update={(comment, types: any, hypothesisSupporters, rating, isChanged:boolean) => {
          setCreateSaving(true);

          const { user } = props;
          let insightInsertionType=_finding?.findingInsertionType;
          
          if(_finding?.findingInsertionType==="AI" && isChanged){
            insightInsertionType="Combined";
          }

          const findingDetails = {
            editor: "material", // used to calculate newline offsets. Only for notesEditor ATTOW.
            artifactID: getCurrentArtifactID(base, artifacts),
            audioBegin: __quote?.audioBegin,
            audioEnd: __quote?.audioEnd,
            begin: props.begin,
            findingID: _finding?.findingID || _finding?.id || "",
            end: props.end,
            comment: comment,
            created: new Date(),
            typeID: types,
            quote: __quote,
            userID: user.id,
            interviewID: base.selectedInterview,
            hypothesisLinks: JSON.stringify(hypothesisSupporters),
            rating,
            isDraft: false,
            sessionID: discoverySession?.sessionID,
            findingInsertionType: _finding?.findingInsertionType,
            isChanged,
          };

          updateFinding(findingDetails).then((value) => {
            setCreateSaving(false);
            const finding = { ...findingDetails,...value, hypothesisLinks: hypothesisSupporters,findingInsertionType:insightInsertionType };
            updateBaseFinding(setBase, finding);
          });
        }}
      />
      <AlertDialog
        open={!!insightToDelete}
        title={DELETE_INSIGHT_MODAL_TEXTS.TITLE}
        description={DELETE_INSIGHT_MODAL_TEXTS.DESCRIPTION}
        confirmButton={
          <Button
            variant="outlined"
            endIcon={
              deletingInsight ? <CircularProgress size={16} /> : null
            }
            onClick={() => {
              deleteInsight(insightToDelete);
            }}
          >
            Yes
          </Button>
        }
        abortButton={
          <Button
            variant="contained"
            onClick={() => {
              setInsightToDelete(null);
            }}
          >
            No
          </Button>
        }
      />
      </>
    );
}
